import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/admin/BookingsTab";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Eye, ShieldCheck, Clock, User2, Copy } from "lucide-react";

const StatusPill = ({ status }) => {
  const map = {
    pending: "bg-amber-400/10 border-amber-400/30 text-amber-300",
    approved: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
    rejected: "bg-red-500/10 border-red-500/30 text-red-300",
  };
  const Icon = status === "approved" ? CheckCircle2 : status === "rejected" ? XCircle : Clock;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest border ${map[status] || map.pending}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
};

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export default function DriverApplicationsTab({ isSuper }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");
  const [approved, setApproved] = useState(null);

  const load = async () => {
    try {
      const q = filter === "all" ? "" : `?status_filter=${filter}`;
      const { data } = await api.get(`/admin/driver-applications${q}`);
      setData(data);
    } catch { toast.error("Failed to load applications"); }
  };
  useEffect(() => { load(); }, [filter]);

  const openDetail = async (row) => {
    setSelected(row);
    setDetail(null); setRejectMode(false); setReason(""); setApproved(null);
    try {
      const { data } = await api.get(`/admin/driver-applications/${row.id}`);
      setDetail(data);
    } catch { toast.error("Failed to load application"); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/driver-applications/${selected.id}/approve`);
      setApproved(data);
      toast.success(`Approved — driver ${data.driver_id.slice(0, 8)} created`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/driver-applications/${selected.id}/reject`, { reason });
      toast.success("Application rejected");
      setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;

  return (
    <div data-testid="driver-applications-tab">
      <Header title="Driver applications" subtitle="Review self-enrollments, verify documents, approve or reject." />

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`apps-filter-${f}`}
            className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${
              filter === f ? "bg-amber-400 text-[#090D14]" : "bg-black/30 border border-white/10 text-slate-400 hover:text-white"
            }`}
          >
            {f} {data.counts[f] !== undefined ? `(${data.counts[f]})` : ""}
          </button>
        ))}
      </div>

      <div className="glass-card rounded-2xl overflow-hidden gold-border">
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-slate-500 uppercase tracking-widest text-[10px] font-mono">
              <tr>
                <th className="text-left px-4 py-3 font-normal">Ref</th>
                <th className="text-left px-4 py-3 font-normal">Applicant</th>
                <th className="text-left px-4 py-3 font-normal">Vehicle</th>
                <th className="text-left px-4 py-3 font-normal">Submitted</th>
                <th className="text-left px-4 py-3 font-normal">Status</th>
                <th className="text-right px-4 py-3 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.applications.length === 0 && (
                <tr><td colSpan="6" className="text-center text-slate-500 py-10" data-testid="apps-empty">No applications yet.</td></tr>
              )}
              {data.applications.map((a) => (
                <tr key={a.id} className="border-t border-white/5" data-testid={`apps-row-${a.reference}`}>
                  <td className="px-4 py-3 text-amber-300 font-mono">{a.reference}</td>
                  <td className="px-4 py-3">
                    <div className="text-white">{a.name}</div>
                    <div className="text-xs text-slate-500">{a.email}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">
                    <div>{a.vehicle_year} {a.vehicle_make} {a.vehicle_model}</div>
                    <div className="text-xs text-slate-500 font-mono">{a.vehicle_plate}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs font-mono">{fmt(a.submitted_at)}</td>
                  <td className="px-4 py-3"><StatusPill status={a.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" onClick={() => openDetail(a)} variant="outline" className="bg-transparent border-white/10 text-slate-300" data-testid={`apps-view-${a.reference}`}>
                      <Eye className="w-3.5 h-3.5 mr-1.5" /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className="bg-[#111827] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <User2 className="w-5 h-5 text-amber-400" />
              {selected?.name} <StatusPill status={selected?.status || "pending"} />
            </DialogTitle>
          </DialogHeader>
          {!detail ? <div className="text-slate-400">Loading…</div> : (
            <div className="space-y-5">
              {approved && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                  <div className="text-emerald-300 font-semibold mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Approved · credentials emailed</div>
                  <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                    <div><span className="text-slate-400">Email: </span>{detail.email}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">Temp pw:</span>
                      <span className="text-amber-300">{approved.temp_password}</span>
                      <button onClick={() => { navigator.clipboard.writeText(approved.temp_password); toast.success("Copied"); }} className="text-amber-400 hover:text-amber-300"><Copy className="w-3 h-3" /></button>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <Info k="Reference" v={detail.reference} mono />
                <Info k="Submitted" v={fmt(detail.submitted_at)} />
                <Info k="Email" v={detail.email} />
                <Info k="Phone" v={detail.phone} />
                <Info k="Licence" v={`${detail.license_number} · exp ${detail.license_expiry}`} />
                <Info k="Insurance exp" v={detail.insurance_expiry} />
                <Info k="Vehicle" v={`${detail.vehicle_year} ${detail.vehicle_make} ${detail.vehicle_model}`} />
                <Info k="Plate" v={detail.vehicle_plate} mono />
              </div>
              {detail.notes && (
                <div className="bg-black/30 border border-white/5 rounded p-3 text-sm text-slate-300">{detail.notes}</div>
              )}
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-2">Documents</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[["doc_license", "Licence"], ["doc_insurance", "Insurance"], ["doc_profile", "Profile"], ["doc_vehicle", "Vehicle"]].map(([k, label]) => (
                    <div key={k} className="bg-black/30 border border-white/5 rounded-lg p-2">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">{label}</div>
                      {detail[k] ? (
                        <a href={detail[k]} target="_blank" rel="noreferrer" data-testid={`apps-doc-${k}`}>
                          <img src={detail[k]} alt={label} className="w-full h-24 object-cover rounded border border-white/10" />
                        </a>
                      ) : <div className="h-24 grid place-items-center text-slate-600 text-xs">— none —</div>}
                    </div>
                  ))}
                </div>
              </div>
              {detail.status === "rejected" && detail.reject_reason && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <Badge className="bg-red-500/20 text-red-300 border-red-500/40">Rejected {detail.reviewed_at && `· ${fmt(detail.reviewed_at)}`}</Badge>
                  <div className="text-sm text-slate-300 mt-2">{detail.reject_reason}</div>
                </div>
              )}
              {detail.status === "pending" && isSuper && (
                <div className="border-t border-white/5 pt-4">
                  {!rejectMode ? (
                    <div className="flex gap-2 justify-end">
                      <Button onClick={() => setRejectMode(true)} disabled={busy} variant="outline" className="bg-transparent border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="apps-reject-toggle">
                        <XCircle className="w-4 h-4 mr-2" /> Reject
                      </Button>
                      <Button onClick={approve} disabled={busy} className="btn-gold" data-testid="apps-approve">
                        <CheckCircle2 className="w-4 h-4 mr-2" /> Approve & create driver
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for rejection (emailed to applicant)…" className="bg-black/30 border-white/10 min-h-[80px]" data-testid="apps-reject-reason" />
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" className="bg-transparent border-white/10 text-slate-300" onClick={() => setRejectMode(false)}>Cancel</Button>
                        <Button onClick={reject} disabled={busy || !reason.trim()} className="bg-red-500 hover:bg-red-600 text-white" data-testid="apps-reject-confirm">Confirm reject</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ k, v, mono }) => (
  <div className="bg-black/20 border border-white/5 rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{k}</div>
    <div className={`text-slate-100 text-sm mt-0.5 ${mono ? "font-mono" : ""}`}>{v || "—"}</div>
  </div>
);
